<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { DateTime } from "luxon";
import Select from 'primevue/select';
import MetricsSection from "@/components/metrics/MetricsSection.vue";
import MetricsLocChart from "@/components/metrics/MetricsLocChart.vue";
import MetricsLocTable from "@/components/metrics/MetricsLocTable.vue";
import MetricsAllocationChart from "@/components/metrics/MetricsAllocationChart.vue";
import MetricsAiContributionChart from "@/components/metrics/MetricsAiContributionChart.vue";
import DateRangeControls from "@/components/metrics/DateRangeControls.vue";
import DepartmentFilter from "@/components/metrics/DepartmentFilter.vue";
import GroupingControls from "@/components/metrics/GroupingControls.vue";
import LocChartGroupingControls from "@/components/metrics/LocChartGroupingControls.vue";
import { peopleBackend } from "@/backend/people.backend";
import metricsBackend, { type PRData } from '@/backend/metrics.backend';
import type { Team } from "@/backend/teams.backend";

interface Props {
  // When provided, shows metrics for a specific team
  team?: Team;
  // When provided, shows metrics for a specific user
  userId?: number;
  // Whether to show the department filter (hidden in team-specific mode)
  showDepartmentFilter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  team: undefined,
  userId: undefined,
  showDepartmentFilter: true,
});

// Initialize with this week as default
const now = DateTime.now();
const startOfWeek = now.startOf('week');
const endOfWeek = now.endOf('week');
const dateRange = ref({
  from: startOfWeek.toJSDate(),
  to: endOfWeek.toJSDate()
});

// Initialize with all departments
const selectedDepartment = ref('all');
const departments = ref<any[]>([]);

// Chart control states (preserved across period/department changes)
const locGroupBy = ref<'day' | 'week'>('day');
const locChartGroupBy = ref<'additions-deletions' | 'repo' | 'github-username' | 'ai-contribution' | 'team'>('additions-deletions');
const aiContributionAggregation = ref<'pr-count' | 'total-loc'>('pr-count');

// LOC Table control states (preserved across period/department changes)
const locTableRowDimension = ref<'repo' | 'github-username' | 'team' | 'ai-contribution'>('github-username');
const locTableColDimension = ref<'repo' | 'github-username' | 'team' | 'ai-contribution'>('repo');

const dimensionOptions = [
  { value: 'repo' as const, label: 'Repository', icon: 'pi pi-box' },
  { value: 'github-username' as const, label: 'GitHub User', icon: 'pi pi-user' },
  { value: 'team' as const, label: 'Team', icon: 'pi pi-users' },
  { value: 'ai-contribution' as const, label: 'AI Contribution', icon: 'pi pi-sparkles' }
];

// LOC data fetching (shared between LOC chart and AI contribution chart)
const locDataLoading = ref(false);
const locDataError = ref<string | null>(null);
const locData = ref<PRData[]>([]);

// Fetch LOC data function
async function fetchLocData() {
  if (locDataLoading.value) return;
  
  locDataLoading.value = true;
  locDataError.value = null;
  
  try {
    const fromStr = DateTime.fromJSDate(dateRange.value.from).toISODate() || '';
    const toStr = DateTime.fromJSDate(dateRange.value.to).toISODate() || '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const department = props.team || props.userId ? undefined : selectedDepartment.value;
    const teamId = props.team?.id;
    const userId = props.userId;
    
    const response = await metricsBackend.getLOCData(fromStr, toStr, timezone, teamId, department, userId);
    locData.value = response.data;
  } catch (err) {
    locDataError.value = err instanceof Error ? err.message : 'Failed to fetch LOC data';
    locData.value = [];
  } finally {
    locDataLoading.value = false;
  }
}

// Watch for changes that require refetching LOC data
watch([() => dateRange.value.from, () => dateRange.value.to, () => selectedDepartment.value, () => props.team?.id, () => props.userId], () => {
  fetchLocData();
});

// Retry function for LOC data
function retryLocData() {
  fetchLocData();
}


// Computed description for work allocation section
const allocationDescription = computed(() => {
  // User-specific mode
  if (props.userId) {
    return "Personal time allocation breakdown from pulse entries for the selected period";
  }
  // Team-specific mode
  if (props.team) {
    return `${props.team.name} time allocation breakdown from pulse entries for the selected period`;
  }
  
  // Department filter mode
  const baseDescription = "Team time allocation breakdown from pulse entries for the selected period";
  if (selectedDepartment.value === 'all') {
    return baseDescription;
  }
  const dept = departments.value.find(d => d.id.toString() === selectedDepartment.value);
  const deptName = dept ? dept.name : 'Selected Department';
  return `${baseDescription} (${deptName} only)`;
});

// Computed description for LOC section
const locDescription = computed(() => {
  // User-specific mode
  if (props.userId) {
    return "Lines of code changes from your merged pull requests";
  }
  // Team-specific mode
  if (props.team) {
    return `Lines of code changes from merged pull requests for ${props.team.name}`;
  }
  
  // Department filter mode
  const baseDescription = "Lines of code changes from merged pull requests";
  if (selectedDepartment.value === 'all') {
    return baseDescription;
  }
  const dept = departments.value.find(d => d.id.toString() === selectedDepartment.value);
  const deptName = dept ? dept.name : 'Selected Department';
  return `${baseDescription} (${deptName} only)`;
});

// Computed description for AI Contribution section
const aiContributionDescription = computed(() => {
  // User-specific mode
  if (props.userId) {
    return "AI contribution breakdown for your pull requests";
  }
  // Team-specific mode
  if (props.team) {
    return `AI contribution breakdown for ${props.team.name}'s pull requests`;
  }
  
  // Department filter mode
  const baseDescription = "AI contribution breakdown for pull requests";
  if (selectedDepartment.value === 'all') {
    return baseDescription;
  }
  const dept = departments.value.find(d => d.id.toString() === selectedDepartment.value);
  const deptName = dept ? dept.name : 'Selected Department';
  return `${baseDescription} (${deptName} only)`;
});

// Computed description for LOC Table section
const locTableDescription = computed(() => {
  // User-specific mode
  if (props.userId) {
    return "Cross-tabulation of your LOC data by different dimensions";
  }
  // Team-specific mode
  if (props.team) {
    return `Cross-tabulation of LOC data for ${props.team.name} by different dimensions`;
  }
  
  // Department filter mode
  const baseDescription = "Cross-tabulation of LOC data by different dimensions";
  if (selectedDepartment.value === 'all') {
    return baseDescription;
  }
  const dept = departments.value.find(d => d.id.toString() === selectedDepartment.value);
  const deptName = dept ? dept.name : 'Selected Department';
  return `${baseDescription} (${deptName} only)`;
});

// Load departments on mount (only if department filter is shown)
onMounted(async () => {
  if (props.showDepartmentFilter && !props.team && !props.userId) {
    try {
      departments.value = await peopleBackend.departments() as any[];
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  }
  
  // Initial fetch of LOC data
  await fetchLocData();
});
</script>

<template>
  <div class="metrics-content">
    <!-- Global Controls -->
    <div class="sticky top-0 z-10 flex flex-wrap gap-4 items-center justify-between mb-2 p-4 bg-surface-card rounded-lg border border-surface-border bg-surface">
      <DateRangeControls v-model="dateRange" />
      <DepartmentFilter v-if="showDepartmentFilter && !team && !userId" v-model="selectedDepartment" />
    </div>

    <!-- Two Pie Charts Side by Side -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <!-- Work Allocation Section -->
      <MetricsSection
        title="Work Allocation"
        :description="allocationDescription"
      >
        <MetricsAllocationChart
          :from="dateRange.from"
          :to="dateRange.to"
          :department="team || userId ? undefined : selectedDepartment"
          :team-id="team?.id"
          :user-id="userId"
          :key="dateRange.from + '-' + dateRange.to + '-' + (userId || team?.id || selectedDepartment) + '-allocation'"
        />
      </MetricsSection>

      <!-- AI Contribution Section -->
      <MetricsSection
        title="AI Contribution"
        :description="aiContributionDescription"
      >
        <template #controls>
          <Select
            v-model="aiContributionAggregation"
            :options="[
              { value: 'pr-count', label: 'By PR Count', icon: 'pi pi-github' },
              { value: 'total-loc', label: 'By Total LOC', icon: 'pi pi-chart-bar' }
            ]"
            optionLabel="label"
            optionValue="value"
            placeholder="Select aggregation"
            size="small"
            class="w-44"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center gap-2">
                <i :class="slotProps.value === 'pr-count' ? 'pi pi-github' : 'pi pi-chart-bar'" class="text-sm"></i>
                <span>{{ slotProps.value === 'pr-count' ? 'By PR Count' : 'By Total LOC' }}</span>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-2">
                <i :class="slotProps.option.icon" class="text-sm"></i>
                <span>{{ slotProps.option.label }}</span>
              </div>
            </template>
          </Select>
        </template>
        <MetricsAiContributionChart
          :pr-data="locData"
          :loading="locDataLoading"
          :error="locDataError"
          v-model:aggregation-mode="aiContributionAggregation"
          @retry="retryLocData"
          :key="dateRange.from + '-' + dateRange.to + '-' + (userId || team?.id || selectedDepartment) + '-ai-contribution'"
        />
      </MetricsSection>
    </div>

    <!-- Lines of Code Section Full Width -->
    <MetricsSection
      title="Lines of Code"
      :description="locDescription"
    >
      <template #controls>
        <div class="flex gap-2 items-center">
          <GroupingControls v-model="locGroupBy" />
          <LocChartGroupingControls v-model="locChartGroupBy" />
        </div>
      </template>
      <MetricsLocChart
        :from="dateRange.from"
        :to="dateRange.to"
        :pr-data="locData"
        :loading="locDataLoading"
        :error="locDataError"
        :key="dateRange.from + '-' + dateRange.to + '-' + (userId || team?.id || selectedDepartment)"
        v-model:group-by="locGroupBy"
        v-model:loc-group-by="locChartGroupBy"
        @retry="retryLocData"
      />
    </MetricsSection>

    <!-- LOC Cross-Tabulation Table Section -->
    <MetricsSection
      title="LOC Cross-Tabulation"
      :description="locTableDescription"
      class="mt-4"
    >
      <template #controls>
        <div class="flex flex-wrap gap-4 items-center">
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">Rows:</label>
            <Select
              v-model="locTableRowDimension"
              :options="dimensionOptions"
              optionLabel="label"
              optionValue="value"
              size="small"
              class="w-44"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value" class="flex items-center gap-2">
                  <i :class="dimensionOptions.find(opt => opt.value === slotProps.value)?.icon" class="text-sm"></i>
                  <span>{{ dimensionOptions.find(opt => opt.value === slotProps.value)?.label }}</span>
                </div>
              </template>
              <template #option="slotProps">
                <div class="flex items-center gap-2">
                  <i :class="slotProps.option.icon" class="text-sm"></i>
                  <span>{{ slotProps.option.label }}</span>
                </div>
              </template>
            </Select>
          </div>
          
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">Columns:</label>
            <Select
              v-model="locTableColDimension"
              :options="dimensionOptions"
              optionLabel="label"
              optionValue="value"
              size="small"
              class="w-44"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value" class="flex items-center gap-2">
                  <i :class="dimensionOptions.find(opt => opt.value === slotProps.value)?.icon" class="text-sm"></i>
                  <span>{{ dimensionOptions.find(opt => opt.value === slotProps.value)?.label }}</span>
                </div>
              </template>
              <template #option="slotProps">
                <div class="flex items-center gap-2">
                  <i :class="slotProps.option.icon" class="text-sm"></i>
                  <span>{{ slotProps.option.label }}</span>
                </div>
              </template>
            </Select>
          </div>
        </div>
      </template>
      <MetricsLocTable
        :pr-data="locData"
        :loading="locDataLoading"
        :error="locDataError"
        :row-dimension="locTableRowDimension"
        :col-dimension="locTableColDimension"
        @retry="retryLocData"
        :key="dateRange.from + '-' + dateRange.to + '-' + (userId || team?.id || selectedDepartment) + '-table'"
      />
    </MetricsSection>
  </div>
</template>

<style scoped>
.metrics-content {
  width: 100%;
  padding-bottom: 20rem;
}
</style>
